import { useState, useEffect, useRef } from "react";
import { Button, Tabs, Table, Card } from "antd";
import { Flex } from "@chakra-ui/layout";
import { MdAssignment, MdLocationOn, MdInventory, MdCheckCircle, MdArrowBack } from "react-icons/md";
import { useFilters } from "core/features/filters/store";
import useStore from "../../../OrdemServico/hooks/useStore";
import { Apontamento } from "../../../OrdemServico/hooks/useGetApontamentoTalhoes";
import { getColumnsLocais, getColumnsRecursos } from "./columns";
import { ConsultarEstoqueButton, usePrevisaoEstoqueOs } from "../../../OrdemServico/consultarEstoque";
import type { InsumoSelectPayload } from "../../../OrdemServico/consultarEstoque/types";
import {
  StyledModal,
  Title,
  StyledTabs,
  CardTitle,
  CardDescription,
  ButtonStyled,
  ButtonStyledLink,
  ContentButton,
} from "./styles";
import { OrdemServico } from "../../../OrdemServico/hooks/useGetOrdemServico";

const { TabPane } = Tabs;

interface ModalLocaisRecursosProps {
  isOpen: boolean;
  loading: boolean;
  data: any;
  listData: any[];
  onClose: () => void;
  back: () => void;
  handleCreate: (locais: any[], recursos: any[]) => void;
  isEdit?: boolean;
  orderedResult: any[];
  ordemServicoData?: OrdemServico;
  statusBlock?: boolean;
  apontamentoTalhoes?: Apontamento[];
  unidadeIdExterno?: string;
}

function ModalLocaisRecursos({
  isOpen,
  loading,
  data,
  onClose,
  back,
  handleCreate,
  isEdit,
  orderedResult,
  ordemServicoData,
  statusBlock,
  listData,
  apontamentoTalhoes,
  unidadeIdExterno,
}: ModalLocaisRecursosProps) {

  const [locais, setLocais] = useState<any[]>([]);
  const [recursos, setRecursos] = useState<any[]>([]);
  const [isRecursosValid, setIsRecursosValid] = useState(false);
  const [searchComponent, setSearchComponent] = useState<Record<number, string>>({});
  const refDose = useRef(null);

  const { planoOperacionalOperacaoTalhaoIds: operacoes } = useStore();
  const { filters } = useFilters();
  const fazendaId = filters.unidade?.id;

  const previsao = usePrevisaoEstoqueOs({
    recursos,
    locais,
    fazendaId,
    ordemServicoId: isEdit ? ordemServicoData?.ordemServicoId : undefined,
  });

  let ocupacaoId = data?.[0]?.planoOperacionalOperacaoTalhao?.filter((item: any) => operacoes.includes(item.planoOperacionalOperacaoTalhaoId))
  ocupacaoId = ocupacaoId?.[0]?.ocupacaoId ?? "";

  function handleCalculateAreaEfetiva(local: any, key: string, value: string) {
    if (key === "tipo" && value === "porcentagem") {
      return local.areaPlantadaHa;
    }
    if (key === "tipo" && value === "area") {
      return local.areaPlantadaHa;
    }
    if (key === "valorExecutar" && local.tipo === "area") {
      return value;
    }
    if (key === "valorExecutar" && local.tipo === "porcentagem") {
      return local.areaPlantadaHa * (Number(value) / 100);
    }
  }

  function handleSetValorExecutar(local: any, key: string, value: string) {
    if (key === "tipo" && value === "porcentagem") {
      return 100;
    }
    if (key === "tipo" && value === "area") {
      return local.areaPlantadaHa;
    }
    if (key === "valorExecutar") {
      return value;
    }
    return local.valorExecutar;
  }

  function handleChangeLocal(talhaoId: string, key: string, value: string) {
    const result = locais?.map(local => {
      if (local.talhaoId === talhaoId || local.talhaoIdExterno === talhaoId) {
        return {
          ...local,
          [key]: value,
          areaEfetiva: handleCalculateAreaEfetiva(local, key, value),
          valorExecutar: handleSetValorExecutar(local, key, value),
        }
      }
      return local;
    });
    setLocais(result);
  }

  function handleDeleteLocal(talhaoId: string) {
    const result = locais.filter(local => local.talhaoId !== talhaoId);
    setLocais(result);
  }

  function handleChangeRecurso(
    index: number,
    key: string,
    value: string | string[] | InsumoSelectPayload,
  ) {
    setRecursos(prevRecursos => {
      const result = prevRecursos?.map((recurso, i) => {
        if (i === index) {
          if (key === "insumo") {
            const payload = value as InsumoSelectPayload;
            return {
              ...recurso,
              insumoIdExterno: payload.insumoIdExterno,
              insumoId: payload.insumoId,
              unidade: payload.unidade,
              insumoUnidadeMedidaDescricao: payload.insumoUnidadeMedidaDescricao,
              insumoLabel: payload.insumoLabel,
            };
          }
          return {
            ...recurso,
            [key]: value,
            ...(key === "componenteIdExterno" ? {
              insumoIdExterno: null,
              insumoId: null,
              insumoLabel: null,
              insumoUnidadeMedidaDescricao: null,
              unidade: null,
            } : {}),
          };
        }
        return recurso;
      });

      const isInputsValid = result?.every(
        i => i.quantidadeProducao !== null &&
          i.insumoIdExterno !== null &&
          i.alvoIdsExternos !== null &&
          i.quantidade !== null &&
          i.quantidade > 0 &&
          i.porcentagemUtilizacao !== null
      );
      const isComponentesValid = new Set(result?.map(item => item.componenteIdExterno)).size === result?.length;
      setIsRecursosValid(isInputsValid && isComponentesValid);
      return result;
    });
  }

  function handleDeleteRecurso(position: number) {
    const result = recursos.filter((_, index) => index !== position);
    const isInputsValid = result?.every(i => i.quantidadeProducao !== null && i.insumoIdExterno !== null && i.alvoIdsExternos !== null);
    const isComponentesValid = new Set(result?.map(item => item.componenteIdExterno)).size === result?.length;
    setRecursos(result);
    setIsRecursosValid(isInputsValid && isComponentesValid);
  }

  function handleAddComponente() {
    const componente = {
      id: Date.now(),
      componenteId: undefined,
      componenteIdExterno: undefined,
      quantidade: 0,
      porcentagemUtilizacao: 100,
      alvoIds: [],
      alvoIdsExternos: [],
      isDeleted: false,
    }
    setRecursos(prev => [...prev, componente]);
  }

  interface ComponenteItem {
    componenteId: string;
    componenteIdExterno: string;
    quantidade: number | null;
    porcentagemUtilizacao: number | null;
    alvoIds: string[];
    alvoIdsExternos: string[];
    id: number;
    unidade: string | null;
    insumoIdExterno: string | null;
    quantidadeProducao: number | null;
  }

  function unificarPorComponenteId(data: ComponenteItem[]): ComponenteItem[] {
    const grupos: Record<string, ComponenteItem[]> = data.reduce(
      (acc, item) => {
        if (!acc[item.componenteId]) {
          acc[item.componenteId] = [];
        }
        acc[item.componenteId].push(item);
        return acc;
      },
      {} as Record<string, ComponenteItem[]>
    );

    return Object.keys(grupos).map((componenteId) => {
      const grupo = grupos[componenteId];
      const primeiro = grupo[0];

      const primeiraQuantidade = grupo[0].quantidade;
      const primeiraPorcentagemUtilizacao = grupo[0].porcentagemUtilizacao;
      const primeiraQuantidadeProducao = grupo[0].quantidadeProducao;

      const todasQuantidadesIguais = grupo.every(
        (item) => item.quantidade === primeiraQuantidade
      );

      const todasPorcentagemUtilizacaoIguais = grupo.every(
        (item) => item.porcentagemUtilizacao === primeiraPorcentagemUtilizacao
      );

      const todasQuantidadeProducaoIguais = grupo.every(
        (item) => item.quantidadeProducao === primeiraQuantidadeProducao
      );

      const alvoIdsMap: Record<string, boolean> = {};
      const alvoIds: string[] = [];

      grupo.forEach((item) => {
        (item.alvoIds || []).forEach((alvoId) => {
          if (!alvoIdsMap[alvoId]) {
            alvoIdsMap[alvoId] = true;
            alvoIds.push(alvoId);
          }
        });
      });

      const alvoIdsExternosMap: Record<string, boolean> = {};
      const alvoIdsExternos: string[] = [];

      grupo.forEach((item) => {
        (item.alvoIdsExternos || []).forEach((alvoIdExterno) => {
          if (!alvoIdsExternosMap[alvoIdExterno]) {
            alvoIdsExternosMap[alvoIdExterno] = true;
            alvoIdsExternos.push(alvoIdExterno);
          }
        });
      });

      return {
        ...primeiro,
        quantidade: todasQuantidadesIguais ? primeiraQuantidade : null,
        porcentagemUtilizacao: todasPorcentagemUtilizacaoIguais ? primeiraPorcentagemUtilizacao : null,
        quantidadeProducao: todasQuantidadeProducaoIguais ? primeiraQuantidadeProducao : null,
        alvoIds,
        alvoIdsExternos,
      };
    });
  }

  const lastBuiltDataRef = useRef<any>(null);
  const lastBuiltOperacoesKeyRef = useRef<string | null>(null);

  const findCodeArea = (talhao: any, codeAreaId: string) => {
    if (isEdit && ordemServicoData?.talhoes?.find(t => t?.talhaoId === codeAreaId)) {
      return ordemServicoData?.talhoes?.find(t => t?.talhaoId === codeAreaId)?.area
    }

    let area = talhao?.planoOperacionalOperacaoTalhao
      ?.find((operacao: any) => operacao.areaDisponivelHa > 0)
      ?.areaDisponivelHa;

    if (!area) {
      area = talhao?.areaPlantadaHa;
    }

    return area;
  };

  useEffect(() => {
    if (!data || !isOpen) return;

    const temTalhaoComApontamento = apontamentoTalhoes?.some(a => a?.possuiApontamento);
    const operacoesKey = JSON.stringify([...operacoes].sort());
    const isSameBuild = lastBuiltDataRef.current === data && lastBuiltOperacoesKeyRef.current === operacoesKey;

    if (isSameBuild) return;

    lastBuiltDataRef.current = data;
    lastBuiltOperacoesKeyRef.current = operacoesKey;

    if (data && data.length === 0 && listData) {
      const componentes = ordemServicoData?.componentes?.map(item => {
        return {
          ...item,
          apontamento: temTalhaoComApontamento,
          porcentagemUtilizacao: item.quantidadeUtilizacao,
        }
      }) ?? [];
      const locais = listData?.map(item => {
        const apontamento = apontamentoTalhoes?.find(a => a.talhaoIdExterno === item?.talhaoIdExterno);
        return {
          ...item,
          isDeleted: false,
          tipo: "porcentagem",
          valorExecutar: 100,
          areaEfetiva: Number(item.areaPlantadaHa),
          areaPlantadaHa: Number(item.areaPlantadaHa),
          apontamento: apontamento?.possuiApontamento,
        }
      });
      setLocais(locais);
      setRecursos(componentes);

      const isValid = componentes?.every(
        i => i.quantidadeProducao !== null &&
          i.quantidade !== null &&
          i.quantidade > 0 &&
          i.porcentagemUtilizacao !== null &&
          i.componenteIdExterno !== null
      );
      setIsRecursosValid(isValid);
      return;
    }

    let talhoes = data?.filter(
      (t: any) => t.planoOperacionalOperacaoTalhao.some(
        (p: any) => operacoes.includes(p.planoOperacionalOperacaoTalhaoId)
      )
    )?.map((talhao: any) => {
      return {
        ...talhao,
        planoOperacionalOperacaoTalhao: talhao.planoOperacionalOperacaoTalhao.filter((o: any) => {
          return operacoes.includes(o.planoOperacionalOperacaoTalhaoId)
        })
      }
    });

    if (operacoes.length === 0) {
      talhoes = data?.map((talhao: any) => {
        return {
          ...talhao,
          planoOperacionalOperacaoTalhao: []
        }
      })
    }

    const locais = talhoes?.map((talhao: any) => {
      const area = findCodeArea(talhao, talhao?.talhaoId);
      let valorExecutar = 100;
      let isDeleted = isEdit && talhao?.planoOperacionalOperacaoTalhao?.some((t: any) => t?.status?.includes("Executada"));
      const apontamento = apontamentoTalhoes?.find(a => a.talhaoId === talhao?.talhaoId);

      if (isEdit && orderedResult.some((o: any) => o.codigo === talhao.codigo)) {
        const index = orderedResult.findIndex((o: any) => o.codigo === talhao.codigo);
        orderedResult[index]?.planoOperacionalOperacaoTalhao?.forEach((plan: any, i: number) => {

          const ids = talhao?.planoOperacionalOperacaoTalhao.map((i: any) => i.planoOperacionalOperacaoTalhaoId);

          const atual = orderedResult[index]?.planoOperacionalOperacaoTalhao
            ?.findIndex((operacao: any) => ids.includes(operacao.planoOperacionalOperacaoTalhaoId))

          const talhaoPosterior = orderedResult[index]?.planoOperacionalOperacaoTalhao?.[atual + 1];

          const areaTotal = talhao?.areaPlantadaHa;
          const areaPlantada = ordemServicoData?.talhoes?.find(t => t?.talhaoId === talhao?.talhaoId)?.area ?? 0;

          const percentual = Math.floor((areaPlantada / areaTotal) * 100);
          valorExecutar = areaPlantada ? percentual : 100;

          if (plan?.operacaoClasse === talhaoPosterior?.operacaoClasse && talhaoPosterior?.ordensServico?.length > 0) {
            isDeleted = true;
          }
        });
      }

      return {
        ...talhao,
        tipo: "porcentagem",
        valorExecutar,
        isDeleted: !isDeleted,
        areaEfetiva: area?.toFixed(2) ?? 0,
        apontamento: apontamento?.possuiApontamento,
      }
    });

    let componentes = talhoes
      ?.flatMap((t: any) => t.planoOperacionalOperacaoTalhao)
      ?.flatMap((t: any) => t.componentes);

    if (isEdit) {
      componentes = ordemServicoData?.componentes?.map((comp: any) => {
        const componentItem = componentes?.find((c: any) => c?.componenteId === comp?.componenteId);
        comp.quantidade = comp?.quantidade ?? componentItem.quantidade;
        comp.porcentagemUtilizacao = comp?.quantidadeUtilizacao ?? componentItem.porcentagemUtilizacao;
        comp.porcentagemUtilizacao = comp?.quantidadeUtilizacao ?? componentItem.porcentagemUtilizacao;
        comp.alvoIds = comp?.alvos?.map((a: any) => a?.alvoId) ?? componentItem.alvoIds;
        const alvos = comp?.alvos?.map((a: any) => a?.alvoIdExterno) ?? componentItem?.alvoIdsExternos?.filter((a: string) => a !== null);
        comp.alvoIdsExternos = alvos;
        return {
          ...comp,
          alvoIds: comp?.alvos?.map((a: any) => a?.alvoId) ?? componentItem.alvoIds,
        }
      })
    }

    const recursos = componentes?.map((componente: any, index: number) => {
      if (!isEdit) {
        componente.alvoIdsExternos = componente?.alvoIdsExternos?.filter((a: string) => a !== null)
        componente.quantidadeProducao = 1;
      }
      return {
        ...componente,
        id: index,
        isDeleted: statusBlock,
        apontamento: temTalhaoComApontamento,
        unidade: isEdit ? (componente.unidade ?? null) : null,
        insumoId: isEdit ? (componente.insumoId ?? null) : null,
        insumoLabel: isEdit ? (componente.insumoLabel ?? null) : null,
        insumoIdExterno: isEdit ? (componente.insumoIdExterno ?? null) : null,
      }
    });

    setLocais(locais);
    setRecursos(unificarPorComponenteId(recursos));

    const isValid = recursos?.every(
      (i: any) => i.quantidadeProducao !== null &&
        i.quantidade !== null &&
        i.quantidade > 0 &&
        i.porcentagemUtilizacao !== null &&
        i.componenteIdExterno !== null
    );
    setIsRecursosValid(isValid);
  }, [data, isOpen, operacoes]);

  return (
    <StyledModal
      centered
      width={"95%"}
      zIndex={999999}
      visible={isOpen}
      onCancel={onClose}
      title={
        <Title>
          <MdAssignment color="#fff" size={28} />
          {isEdit ? "Edição " : "Criação"} de Ordem de Serviço - Matriz de Operações {isEdit && ` · O.S. ${ordemServicoData?.numero}`}
        </Title>
      }
      footer={
        <Flex justifyContent="space-between" alignItems="center">
          <p><b>{operacoes.length ?? 0}</b> operações selecionadas</p>
          <ContentButton>
            <ButtonStyledLink
              size="large"
              onClick={back}
              loading={loading}
              icon={<MdArrowBack size={24} />}
            >Voltar</ButtonStyledLink>
            <ButtonStyled
              type="primary"
              size="large"
              loading={loading}
              onClick={() => handleCreate(locais, recursos)}
              disabled={!isRecursosValid}
              icon={<MdCheckCircle size={24} />}
            >Confirmar e {isEdit ? "Editar" : "Criar"} OS</ButtonStyled>
          </ContentButton>
        </Flex>
      }
    >
      <StyledTabs defaultActiveKey="1" type="card" size="large">
        <TabPane tab="Locais" key="1">
          <Card>
            <CardTitle>
              <MdLocationOn color="#4caf50" />
              <p>Validação de Locais e Percentuais</p>
            </CardTitle>
            <CardDescription>Valide os talhões e ajuste os percentuais de execução conforme necessário.</CardDescription>
            <Table
              columns={getColumnsLocais(handleChangeLocal, isEdit, handleDeleteLocal)}
              dataSource={locais}
              pagination={false}
              scroll={{ x: true }}
            />
          </Card>
        </TabPane>
        <TabPane tab="Recursos" key="2">
          <Card>
            <CardTitle>
              <MdInventory color="#2196f3" />
              <p>Configuração de Recursos por Componente</p>
            </CardTitle>
            <CardDescription>Configure os insumos, doses e alvos agrupados por componente. Alterações aplicam-se a todos os talhões que utilizam o mesmo componente.</CardDescription>
            <Table
              columns={getColumnsRecursos(
                recursos,
                handleDeleteRecurso,
                handleChangeRecurso,
                setSearchComponent,
                searchComponent,
                refDose,
                ocupacaoId,
                isEdit,
                previsao.linhas,
                unidadeIdExterno,
              )}
              dataSource={recursos}
              pagination={false}
              scroll={{ x: true }}
            />
            <Flex marginTop={4} gridGap={3} align="center">
              <Button
                size="large"
                type="primary"
                onClick={handleAddComponente}
                style={{ borderRadius: 10 }}
              >Adicionar outro componente</Button>
              <ConsultarEstoqueButton
                countNegativos={previsao.countNegativos}
                linhas={previsao.linhas}
                isLoading={previsao.isLoading}
              />
            </Flex>
          </Card>
        </TabPane>
      </StyledTabs>
    </StyledModal>
  );
}

export default ModalLocaisRecursos;
